import { Box, Typography, Paper, Chip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";
import { List, ListItem, ListItemText } from "@mui/material";

const WorkPermitSummary = ({ ptwData, attachmentFileNames = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return t("commitment.noItemsSelected");
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getChecklistNames = (checklistIds) => {
    if (!checklistIds || checklistIds.length === 0)
      return [t("commitment.noItemsSelected")];
    return checklistIds.map((checklist) => checklist.label || checklist.value);
  };

  const getGroupedPrecautions = () => {
    if (!ptwData.hazards || ptwData.hazards.length === 0) {
      return {};
    }

    // Group precautions by category across all hazards
    const groupedByCategory = {};
    const precautionIdsSeen = new Set();
    const allPrecautionObjects = {}; // Store all precaution objects by ID

    // First pass: collect selected precautions from categories
    ptwData.hazards.forEach((hazard) => {
      // Get the selected precaution IDs for this hazard
      const selectedPrecautionIds = hazard.precaution_ids || [];

      if (
        hazard.precaution_categories &&
        Array.isArray(hazard.precaution_categories)
      ) {
        hazard.precaution_categories.forEach((category) => {
          if (!groupedByCategory[category.name]) {
            groupedByCategory[category.name] = [];
          }

          if (category.precautions && Array.isArray(category.precautions)) {
            category.precautions.forEach((precaution) => {
              // Store precaution object for later reference
              allPrecautionObjects[precaution.id] = precaution;

              // Only show precautions that are selected (in precaution_ids array)
              if (!selectedPrecautionIds.includes(precaution.id)) {
                return;
              }

              // Deduplicate by precaution id
              if (!precautionIdsSeen.has(precaution.id)) {
                precautionIdsSeen.add(precaution.id);
                groupedByCategory[category.name].push(precaution);
              }
            });
          }
        });
      }
    });

    // Second pass: find uncategorized precautions from precaution_ids
    const unclassifiedPrecautions = [];

    ptwData.hazards.forEach((hazard) => {
      if (hazard.precaution_ids && Array.isArray(hazard.precaution_ids)) {
        hazard.precaution_ids.forEach((precautionId) => {
          // If this precaution ID wasn't seen in categories, it's uncategorized
          if (!precautionIdsSeen.has(precautionId)) {
            // Try to find the precaution object from what we've already seen
            const precautionObject = allPrecautionObjects[precautionId];

            // Only add if we found the precaution object
            if (precautionObject) {
              precautionIdsSeen.add(precautionId);
              unclassifiedPrecautions.push(precautionObject);
            }
          }
        });
      }
    });

    // Add unclassified precautions if any exist
    if (unclassifiedPrecautions.length > 0) {
      groupedByCategory[t("commitment.unclassified", "Unclassified")] =
        unclassifiedPrecautions;
    }

    return groupedByCategory;
  };

  const SummarySection = ({ title, children, icon = "📄" }) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s ease",
        "&:hover": {
          elevation: 4,
          borderColor: theme.palette.primary.main,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1rem",
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: "1.1rem",
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );

  SummarySection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    icon: PropTypes.string,
  };

  const InfoRow = ({ label, value, isChips = false }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 600, mb: 0.5 }}
      >
        {label}:
      </Typography>
      {isChips ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {Array.isArray(value) ? (
            value.map((item, index) => (
              <Chip
                key={index}
                label={item}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.dark, 0.2)
                      : alpha(theme.palette.primary.light, 0.2),
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.dark, 0.3)
                        : alpha(theme.palette.primary.light, 0.3),
                  },
                }}
              />
            ))
          ) : (
            <Chip
              label={value}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.dark, 0.2)
                    : alpha(theme.palette.primary.light, 0.2),
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.dark, 0.3)
                      : alpha(theme.palette.primary.light, 0.3),
                },
              }}
            />
          )}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
          }}
        >
          {value || t("commitment.noItemsSelected")}
        </Typography>
      )}
    </Box>
  );

  InfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    isChips: PropTypes.bool,
  };

  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.primary.main, 0.2)})`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.2)})`,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.75rem",
              color: theme.palette.primary.contrastText,
              boxShadow: theme.shadows[4],
            }}
          >
            📋
          </Box>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: "1.75rem",
            }}
          >
            {t("commitment.summaryTitle")}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontStyle: "italic",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          {t("commitment.summarySubtitle")}
        </Typography>
      </Box>

      {/* Basic Information */}
      <SummarySection title={t("commitment.basicInfo")} icon="ℹ️">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoRow
              label={t("commitment.workflow")}
              value={ptwData.work_flow_id?.label}
            />
            <InfoRow
              label={t("commitment.site")}
              value={ptwData.site_id?.label}
            />
            <InfoRow label={t("commitment.title")} value={ptwData.title} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoRow
              label={t("commitment.startDate")}
              value={formatDate(ptwData.start_datetime)}
            />
            <InfoRow
              label={t("commitment.description")}
              value={ptwData.description}
            />
          </Grid>
        </Grid>
      </SummarySection>

      {/* Team Members */}
      {ptwData.performing_user_ids &&
        ptwData.performing_user_ids.length > 0 && (
          <SummarySection title={t("commitment.teamMembers")} icon="👥">
            <List sx={{ p: 0 }}>
              {ptwData.performing_user_ids.map((member, index) => (
                <ListItem
                  key={member.value || index}
                  sx={{
                    py: 1,
                    px: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <ListItemText
                    primary={member.label || member.value}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { fontWeight: 500 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </SummarySection>
        )}

      {/* Hazards */}
      {ptwData.hazards && ptwData.hazards.length > 0 && (
        <SummarySection title={t("commitment.hazards")} icon="⚠️">
          <List sx={{ p: 0 }}>
            {ptwData.hazards.map((hazard, index) => (
              <ListItem
                key={hazard.id || index}
                sx={{
                  py: 1,
                  px: 0,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <ListItemText
                  primary={hazard.name}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 500 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </SummarySection>
      )}

      {/* Precautions grouped by category */}
      {ptwData.hazards && ptwData.hazards.length > 0 && (
        <SummarySection title={t("commitment.precautions")} icon="🛡️">
          {(() => {
            const groupedPrecautions = getGroupedPrecautions();
            const allCategories = Object.keys(groupedPrecautions);

            // Sort categories to ensure "Unclassified" is always at the end
            const unclassifiedName = t(
              "commitment.unclassified",
              "Unclassified"
            );
            const regularCategories = allCategories.filter(
              (cat) => cat !== unclassifiedName
            );
            const unclassifiedCategory = allCategories.filter(
              (cat) => cat === unclassifiedName
            );
            const categories = [
              ...regularCategories.sort(),
              ...unclassifiedCategory,
            ];

            if (categories.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {t("commitment.noItemsSelected")}
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {categories.map((categoryName) => {
                  const isUnclassified =
                    categoryName ===
                    t("commitment.unclassified", "Unclassified");

                  return (
                    <Box
                      key={categoryName}
                      sx={
                        isUnclassified
                          ? {
                              p: 2,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            }
                          : {}
                      }
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isUnclassified
                            ? theme.palette.warning.dark
                            : theme.palette.primary.main,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {isUnclassified && "⚠️ "}
                        {categoryName}
                      </Typography>
                      {isUnclassified && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mb: 1,
                            fontStyle: "italic",
                          }}
                        >
                          {t(
                            "commitment.unclassifiedNote",
                            "These precautions are not in the current risk register."
                          )}
                        </Typography>
                      )}
                      <List sx={{ p: 0, pl: isUnclassified ? 0 : 2 }}>
                        {groupedPrecautions[categoryName].map((precaution) => (
                          <ListItem
                            key={precaution.id}
                            sx={{
                              py: 0.75,
                              px: 0,
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            <ListItemText
                              primary={precaution.name}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  fontWeight: isUnclassified ? 500 : 400,
                                  textDecoration: isUnclassified
                                    ? "underline"
                                    : "none",
                                  textDecorationColor: isUnclassified
                                    ? alpha(theme.palette.warning.main, 0.6)
                                    : "transparent",
                                  textDecorationThickness: "2px",
                                  textUnderlineOffset: "3px",
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </SummarySection>
      )}

      {/* Checklists */}
      {ptwData.checklist_ids && ptwData.checklist_ids.length > 0 && (
        <SummarySection title={t("commitment.checklists")} icon="✅">
          <InfoRow
            label={t("commitment.checklists")}
            value={getChecklistNames(ptwData.checklist_ids)}
            isChips={true}
          />
        </SummarySection>
      )}

      {/* Attachments */}
      {attachmentFileNames && attachmentFileNames.length > 0 && (
        <SummarySection
          title={t("commitment.attachments") || "Attachments"}
          icon="📎"
        >
          <InfoRow
            label={t("commitment.attachedFiles") || "Attached Files"}
            value={attachmentFileNames}
            isChips={true}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("commitment.fileCount") || "Total files"}:{" "}
              {attachmentFileNames.length}
            </Typography>
          </Box>
        </SummarySection>
      )}
    </Box>
  );
};

WorkPermitSummary.propTypes = {
  ptwData: PropTypes.object.isRequired,
  attachmentFileNames: PropTypes.array,
};

export default WorkPermitSummary;
